import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTemplates } from "../hooks/useTemplates";
import type { Template } from "../types/api";

const NoFilter4: React.FC = () => {
  const navigate = useNavigate();
  const { templates, loading } = useTemplates(1, 12);
  const [featuredTemplates, setFeaturedTemplates] = useState<Template[]>([]);
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());

  // Track page view on mount (disabled for showcase)
  useEffect(() => {
    // Analytics removed for showcase site
  }, []);

  // Load templates
  useEffect(() => {
    if (templates?.templates) {
      setFeaturedTemplates(templates.templates.slice(0, 12));
    }
  }, [templates]);

  const handleTemplateClick = (template: Template) => {
    // Analytics removed for showcase site

    // Navigate to template2v with selected template
    navigate("/video/template2v", {
      state: { template: template },
    });
  };

  const handleBrowseAllClick = () => {
    // Analytics removed for showcase site

    // Navigate to templates page
    navigate("/templates");
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-12">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900 opacity-50" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-5"
          style={{ backgroundImage: "url(/bg.png)" }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full">
          {/* Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/20 rounded-full px-4 py-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span className="text-white text-sm font-semibold">No Limits. No Rules. No Filter.</span>
            </div>
          </div>

          {/* Main Headline */}
          <div className="text-center space-y-6 mb-12">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight">
              Pick Your Fantasy.
              <br />
              Upload <span className="text-white">Anyone</span> 🌶️
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              Choose from dozens of spicy templates. Upload any image. Get your personalized video in 60 seconds.
            </p>
          </div>

          {/* Featured Templates Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              <span className="ml-3 text-white/60">Loading templates...</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-8">
                {featuredTemplates.map((template) => (
                  <div
                    key={template.templateId}
                    onClick={() => handleTemplateClick(template)}
                    className="group relative aspect-square rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/60 transition-all duration-300 cursor-pointer hover:scale-105"
                  >
                    <video
                      ref={(el) => {
                        if (el) {
                          videoRefs.current.set(template.templateId, el);
                        }
                      }}
                      src={template.videoUrl}
                      className="w-full h-full object-cover blur-md"
                      muted
                      loop
                      playsInline
                      autoPlay
                    />
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Click to Use Button */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button className="px-6 py-3 bg-white text-black font-bold rounded-full hover:bg-gray-200 transition-colors">
                        Use This Template
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Browse All Button */}
              <div className="text-center">
                <button
                  onClick={handleBrowseAllClick}
                  className="inline-flex items-center gap-3 bg-white hover:bg-gray-200 text-black text-xl font-bold px-10 py-4 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:shadow-[0_0_50px_rgba(255,255,255,0.5)] hover:scale-105"
                >
                  Browse All Templates
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* How It Works Section */}
      <div className="relative px-4 py-16 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">
              How It Works
            </h2>
            <p className="text-gray-400 text-lg">
              3 simple steps to your custom video
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto border-2 border-white/30">
                <span className="text-3xl font-black text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-white">Pick Your Fantasy</h3>
              <p className="text-gray-400 text-base">
                Browse dozens of spicy templates and choose your favorite scenario
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto border-2 border-white/30">
                <span className="text-3xl font-black text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-white">Upload Any Image</h3>
              <p className="text-gray-400 text-base">
                Upload a photo of anyone - the AI will recreate the template with them
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto border-2 border-white/30">
                <span className="text-3xl font-black text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-white">Get Your Video</h3>
              <p className="text-gray-400 text-base">
                Your personalized video is ready in 60 seconds or less
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="relative px-4 py-16 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Lightning Fast</h3>
              <p className="text-gray-400">
                Your video is ready in 60 seconds or less
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">100% Private</h3>
              <p className="text-gray-400">
                Your images and videos are never shared
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Dozens of Templates</h3>
              <p className="text-gray-400">
                New spicy scenarios added every week
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="relative px-4 py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-4xl md:text-6xl font-black text-white">
            Ready to Create Your Fantasy?
          </h2>
          <p className="text-xl text-gray-300">
            Join thousands creating their perfect videos every day
          </p>
          
          <button
            onClick={handleBrowseAllClick}
            className="inline-flex items-center gap-3 bg-white hover:bg-gray-200 text-black text-xl font-bold px-12 py-5 rounded-full transition-all duration-300 shadow-[0_0_40px_rgba(255,255,255,0.4)] hover:shadow-[0_0_60px_rgba(255,255,255,0.6)] hover:scale-105"
          >
            Start Creating Now
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>

          <p className="text-sm text-gray-500 pt-4">
            No credit card required to start • 100% private and secure
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="relative px-4 py-8 bg-black border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm leading-relaxed">
            The "No Filter" mode is designed for creative and entertainment purposes. 
            Users are responsible for ensuring their content complies with platform 
            guidelines and applicable laws. PixelMaker does not endorse or take 
            responsibility for user-generated content.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NoFilter4;

